import hash from 'hash.js';
import { v4 as uuidv4 } from 'uuid';

import { getConfig, getQbeTrack, setConfig } from '../../configs/config';
import messages from '../../constants/logMessages.json';
import { AnalyticsTime } from '../../types';
// import sha from 'sha.js';
import { StreetAddress, StreetTypes } from '../../types/address';
import { Tools } from '../../types/tools';
import logging, { getMessage } from '../logging';

const { warnHeading, warnBold } = messages;

/**
 * hashHandler
 * @param hashString {String}
 * @returns {string}
 */
const hashHandler = (hashString: string) => {
  if (!hashString) return '';
  return hash.sha256().update(hashString.toString()).digest('hex');
};

/**
 * uuid
 * @returns {string}
 */
const uuid = (): string => uuidv4();

const getSessionStorage = (key: string) => {
  try {
    const obj = window.sessionStorage.getItem(key);
    return obj ? JSON.parse(obj) : false;
  } catch (e) {
    logging.warn(e);
    return false;
  }
};

const setSessionStorage = (key: string, obj: object) => {
  try {
    const jsonStr = obj && JSON.stringify(obj);
    return window.sessionStorage.setItem(key, jsonStr);
  } catch (e) {
    logging.warn(e);
    return false;
  }
};

const deleteSessionStorage = (key: string) =>
  window.sessionStorage && window.sessionStorage.removeItem(key);

/**
 * Initialise the persistence of analytics milestones and time data.
 * This function is executed when milestonesPersistEnabled mode is enabled.
 * It retrieves analyticsMilestones and analyticsTime from both the current configuration and sessionStorage.
 * Then, it merges the current analytics data with persisted data from sessionStorage.
 * Finally, it sets the merged analyticsMilestones and analyticsTime back to the configuration.
 */
const persistConfigInit = (): void => {
  // Retrieve analyticsMilestones and analyticsTime from getConfig() or set them to empty objects if getConfig() returns undefined
  const { analyticsMilestones = {}, analyticsTime = {} } = getConfig() || {};

  // Retrieve analyticsMilestones and analyticsTime from sessionStorage or set them to empty objects if they don't exist
  const persist = getSessionStorage('persistMilestones') || {
    analyticsMilestones: {},
    analyticsTime: {},
  };

  // Merge current analyticsMilestones and analyticsTime with persisted ones
  const merged = {
    analyticsMilestones: {
      ...analyticsMilestones,
      ...persist.analyticsMilestones,
    },
    analyticsTime: { ...analyticsTime, ...persist.analyticsTime },
  };

  // Set the merged analyticsMilestones and analyticsTime back to the config
  setConfig({
    analyticsMilestones: { ...merged.analyticsMilestones },
    analyticsTime: { ...merged.analyticsTime },
  });
};

// Persist key milestones and time and return a { analyticsMilestones, analyticsTime } object
export const handlePersistedConfig = () => {
  const { analyticsMilestones, analyticsTime } = getConfig() || {};
  const current = { analyticsMilestones, analyticsTime };
  // If enableMilestonesPersist flag in Analytics Wrapper is false, return early with current.
  if (!getConfig()?.enableMilestonesPersist) return current;
  const persist = getSessionStorage('persistMilestones') || {};
  // Session storage taking precedence in merged object as persist will overwrite values in a spread operator
  const merged = {
    analyticsMilestones: {
      ...current.analyticsMilestones,
      ...persist.analyticsMilestones,
    },
    analyticsTime: {
      ...current.analyticsTime,
      ...persist.analyticsTime,
    },
  };
  // Persist merged in Session Storage
  setSessionStorage('persistMilestones', merged);
  return merged;
};

const StreetTypesList = () => {
  const qbeTrack = getQbeTrack();
  return qbeTrack.streetTypeMap ? qbeTrack.streetTypeMap : StreetTypes;
};

declare type StreetType = keyof typeof StreetTypesList;

/**
 * Normalised Hash Address
 * Confluence: https://qbe-appservices.atlassian.net/wiki/spaces/WA/pages/2379284505/Normalised+Hash+Address
 * @param streetNumberName {string} - street number and street name
 * @param suburb {string}
 * @param state {string}
 * @param postCode {string}
 * @returns {string}
 */
const normaliseAddress = (streetAddress: StreetAddress) => {
  const {
    streetNumber = '',
    streetName = '',
    suburb = '',
    state = '',
    postCode = '',
    streetType = '',
  } = streetAddress;

  // Standardise the streetType
  const standardStreetType = Object.keys(StreetTypesList).reduce(
    (streetTypeValue, key) => {
      const regExKey = new RegExp(key);
      return regExKey.test(streetType)
        ? streetType.replace(regExKey, StreetTypesList[key as StreetType])
        : streetTypeValue;
    },
    streetType.toLowerCase()
  );

  // Concatenate and normalize the address
  const fullStreetAddress = (
    streetNumber +
    streetName +
    standardStreetType +
    suburb +
    state +
    postCode
  )
    .toLowerCase()
    .replace(/,|:|-|_|\(|\)|'|\/|\.|\s/g, '');

  // Hash the normalised address
  return hashHandler(fullStreetAddress);
};

/**
 * Normalised Hash Address
 * @param email {string}
 * @returns {string}
 */
const normaliseEmail = (email: string) =>
  hashHandler(email.replace(/\s/gi, '').toLowerCase());

/**
 *
 * @param phone {string}
 * @returns {string}
 */
const normalisePhone = (phone: string) =>
  hashHandler(phone.replace(/\s|-/gi, '').toLowerCase());

const getNow = () => new Date().getTime();

const getTimeElapsedInSeconds = (then: number) =>
  then !== 0 ? (getNow() - then) / 1000 : undefined;

// const timeAppCompletion = () => getTimeElapsedInSeconds(getConfig().startTime);

// Reset Entire Analytics Time Object
const resetAnalyticsTime = (): void => {
  setConfig({ analyticsTime: {} });
};

// Clear Analytics Time Item
const clearAnalyticsTimeItem = (keyToDelete: string): void => {
  const { analyticsTime } = getConfig();

  if (!analyticsTime) {
    logging.warn(
      getMessage('analyticsTimeNotAvailable'),
      warnHeading,
      warnBold
    );
    return;
  }

  if (analyticsTime?.[keyToDelete]) {
    const newObj: AnalyticsTime = { ...analyticsTime };
    delete newObj[keyToDelete];

    setConfig({ analyticsTime: { ...newObj } });
  } else {
    logging.warn(
      getMessage('keyToDeleteNotFound', [keyToDelete]),
      warnHeading,
      warnBold
    );
  }
};

const getAllMilestonesFired = () =>
  handlePersistedConfig()?.analyticsMilestones || {};

const getAllApiMilestonesFired = () =>
  getConfig()?.analyticsApiMilestones || {};

const hasMilestoneFired = (milestoneKey: string) =>
  handlePersistedConfig()?.analyticsMilestones?.[milestoneKey] || false;
const hasApiMilestoneFired = (
  milestoneKey: Record<string, unknown>,
  requestId: string
) => !!milestoneKey?.[requestId];

const setMilestone = (milestoneKey: string) => {
  const { analyticsMilestones } = getConfig();
  setConfig({
    analyticsMilestones: {
      ...analyticsMilestones,
      [milestoneKey]: true,
    },
  });
  handlePersistedConfig();
};

const setApiMilestone = (apiMilestoneKey: string, requestId: string) => {
  const { analyticsApiMilestones } = getConfig();

  if (analyticsApiMilestones?.[apiMilestoneKey]) {
    const existingMilestoneKey = analyticsApiMilestones?.[apiMilestoneKey];
    const { ...existRequestId } = existingMilestoneKey;
    const { [apiMilestoneKey]: _removedApiMilestoneKey, ...restApiMilestones } =
      analyticsApiMilestones;

    const updateRequestedIds = {
      ...existRequestId,
      [requestId]: {
        apiStart: true,
        requestId,
        time: getNow(),
      },
    };

    setConfig({
      analyticsApiMilestones: {
        [apiMilestoneKey]: {
          ...updateRequestedIds,
        },
        ...restApiMilestones,
      },
    });
  } else {
    setConfig({
      analyticsApiMilestones: {
        ...analyticsApiMilestones,
        [apiMilestoneKey]: {
          [requestId]: {
            apiStart: true,
            requestId,
            time: getNow(),
          },
        },
      },
    });
  }
};

const resetMilestone = (milestoneKey: string) => {
  const config = getConfig();

  if (config?.analyticsMilestones) {
    const { [milestoneKey]: _deletedMilestone, ...remainingMilestones } =
      config.analyticsMilestones;
    setConfig({
      ...config,
      analyticsMilestones: remainingMilestones,
    });
    handlePersistedConfig();
  }
};

const resetApiMilestone = (apiName: string, requestId: string) => {
  const { analyticsApiMilestones } = getConfig();

  // Check if analyticsApiMilestones is available and contains the specified apiName
  if (analyticsApiMilestones?.[apiName]) {
    // Destructure the apiMilestones object
    const { [apiName]: apiMilestone, ...restApiMilestones } =
      analyticsApiMilestones;

    // Check if the requestId exists for the specified apiName
    if (apiMilestone && apiMilestone[requestId]) {
      // Destructure the requestId and keep the rest
      const { [requestId]: _deletedRequest, ...restMilestones } = apiMilestone;

      // Update the analyticsApiMilestones with the modified object
      setConfig({
        analyticsApiMilestones: {
          ...restApiMilestones,
          ...(Object.keys(restMilestones).length > 0
            ? { [apiName]: restMilestones }
            : {}),
        },
      });
    }
  }
};

const resetTransactionStartView = (transactionType: string) => {
  const config = getConfig();
  const sanitisedTransactionType = transactionType.replace(
    /[^a-zA-Z0-9]+(.)/g,
    (_: string, group1: string) => group1.toUpperCase()
  );

  const tansactionStartViewTime = `${sanitisedTransactionType}TransactionStartViewTime`;
  if (config?.analyticsTime) {
    const {
      [tansactionStartViewTime]: _deletedTransactionStartTime,
      ...remainingMilestones
    } = config.analyticsTime;

    setConfig({
      ...config,
      analyticsTime: remainingMilestones,
    });
  }
};

/**
 * Removes query parameters from a given URL.
 *
 * @param {string} url - The URL from which to remove query parameters.
 * @returns {string} - The URL without query parameters, or the original input if it's not a valid URL.
 */
export const removeQueryParams = (url: string): string => {
  try {
    const parsedUrl = new URL(url);
    parsedUrl.search = ''; // Clear the query parameters
    return parsedUrl.toString();
  } catch (error) {
    logging.warn('Invalid URL:', error);
    return url; // Return the original input if it's not a valid URL
  }
};

const tools: Tools = {
  hashHandler,
  uuid,
  getSessionStorage,
  setSessionStorage,
  deleteSessionStorage,
  handlePersistedConfig,
  normaliseEmail,
  normalisePhone,
  normaliseAddress,
  getNow,
  getTimeElapsedInSeconds,
  resetAnalyticsTime,
  clearAnalyticsTimeItem,
  getAllMilestonesFired,
  getAllApiMilestonesFired,
  hasApiMilestoneFired,
  hasMilestoneFired,
  setApiMilestone,
  setMilestone,
  resetApiMilestone,
  resetMilestone,
  resetTransactionStartView,
  getConfig,
  persistConfigInit,
  removeQueryParams,
};

export default tools;
